import FAQ from "@/components/FAQ";
import Layout from "@/components/layout";
import { Button } from "@/components/ui/button";
import { cn } from "@/utils/cn";
import Image from "next/image";
import { motion } from "framer-motion";
import { useRouter } from "next/navigation";
import mixpanel from "mixpanel-browser";

const promiseCards = [
  {
    iconUrl: "/verified-icon-purple.svg",
    label: "Verified Experts",
    content:
      "Learn from the best in the industry who have been verified for their expertise.",
  },
  // {
  //   iconUrl: "/location-icon-red.svg",
  //   label: "Live Market Support",
  //   content:
  //     "Get real-time assistance and insights from our experts to stay ahead in the market.",
  // },
  {
    iconUrl: "/gamified-icon.svg",
    label: "Gamified Learning",
    content:
      "Experience learning like never before with our engaging and interactive gamified courses.",
  },
];

const socialProofs = [
  {
    metricNumber: "40K+ ",
    metricTitle: " Hours",
    description: "Of LIVE learning",
  },
  {
    metricNumber: "3 ",
    metricTitle: " Years",
    description: "Of building community",
  },
  {
    metricNumber: "15 ",
    metricTitle: " Lakh+",
    description: "learners attended free Masterclasses",
  },
  {
    metricNumber: "100",
    metricTitle: "+",
    description: "Experts onboarded to deliver best content",
  },
  {
    metricNumber: "50",
    metricTitle: "K+",
    description: "Students learnt and became successful traders",
  },
];

const corePillars = [
  {
    title: "Experts with Verified PnL",
    description: "Learn from pros with proven profits.",
    image: "/core-pillars/expert.gif",
    hex: "#E8FBF5",
  },
  {
    title: "Live Market Support",
    description: "Get real-time expert trading help.",
    image: "/core-pillars/market-support.gif",
    hex: "#FFF6D7",
  },
  {
    title: "Gamified Learning",
    description: "Master trading through fun, interactive games.",
    image: "/core-pillars/gamified-learning.gif",
    hex: "#F3F6FF",
  },
];

const About = () => {
  const router = useRouter();
  return (
    <Layout>
      {/* ----------- Hero ----------- */}
      <motion.section
        initial={{ opacity: 0, translateY: 40 }}
        animate={{ opacity: 1, translateY: 0 }}
        transition={{ duration: 0.355, type: "tween" }}
        className="text-center"
      >
        <Image
          src="/about-hero.svg"
          width={356}
          height={234}
          alt="decorative image"
          className="mx-auto"
        />
        <h1 className="text-xl md:text-2xl font-semibold mt-6">
          Welcome to where possibilities begin
        </h1>
        <p className="text-[#161C2B] leading-[22px] mt-2 mx-auto max-w-lg">
          At TradeWise, we are building the World&apos;s Largest Learning
          Community around the Best Trading experts. We aim to make you an
          efficient trader through LIVE cohort based courses in different
          Trading and Investing domains.
        </p>
      </motion.section>

      {/* ----------- Promises Section ----------- */}
      <motion.section
        initial={{ opacity: 0, translateY: 40 }}
        whileInView={{ opacity: 1, translateY: 0 }}
        transition={{ duration: 0.355, type: "tween", delay: 0.2 }}
        viewport={{ once: true }}
        className="mt-10 text-center"
      >
        <h2 className="text-[#002082] text-xl md:text-3xl font-semibold">
          Our Promises
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 place-items-stretch gap-y-2.5 gap-x-3.5">
          {promiseCards.map((card, index) => (
            <div
              key={index}
              className={`rounded-[13px] px-[17px] py-[22px] flex flex-col justify-center mt-4
                ${
                  index === 0
                    ? "bg-[#f4f2fb]"
                    : index === 1
                    ? "bg-[#f8f3f4]"
                    : "bg-[#eef3fb]"
                }
                `}
            >
              <Image
                src={card.iconUrl}
                alt="decorative icon"
                width={35}
                height={40}
                className="object-contain mx-auto"
              />
              <h4 className="text-[#486284] font-bold text-2xl md:text-xl mt-2">
                {card.label}
              </h4>
              <p className="text-[#8CA2C0] mt-3">{card.content}</p>
            </div>
          ))}
        </div>
      </motion.section>

      {/* ----------- Impact Section ----------- */}
      <section className="w-full relative py-2 md:py-4 mt-3 md:mt-5">
        <div className="grid grid-cols-2 gap-2.5 place-items-stretch mx-auto">
          {socialProofs.map((proof, index) => {
            const isLast = index === socialProofs.length - 1;

            return (
              <div
                key={index}
                className={cn(
                  "bg-[#EFF2F666] hover:bg-[#eeeeee66] p-4 sm:p-5 md:p-6 rounded-lg flex flex-col gap-y-1.5 justify-center items-center w-full transition-all",
                  isLast && "col-span-2"
                )}
              >
                <div className="flex gap-1 text-center text-balance text-xl sm:text-2xl md:text-3xl font-bold text-foreground/85 tracking-wide">
                  <motion.div
                    initial={{ opacity: 0, translateY: 40 }}
                    whileInView={{ opacity: 1, translateY: 0 }}
                    transition={{
                      duration: 0.3,
                    }}
                    viewport={{ once: true }}
                  >
                    {proof.metricNumber}
                  </motion.div>
                  <motion.div
                    initial={{ translateY: 30 }}
                    whileInView={{ translateY: 0 }}
                    transition={{
                      duration: 0.3,
                      delay: 0.3,
                    }}
                    viewport={{ once: true }}
                  >
                    {proof.metricTitle}
                  </motion.div>
                </div>
                <p className="max-sm:text-sm text-balance text-center text-foreground/40 tracking-wide">
                  {proof.description}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ----------- Pillars Section ----------- */}
      <motion.section
        initial={{ opacity: 0, translateY: 40 }}
        whileInView={{ opacity: 1, translateY: 0 }}
        transition={{ duration: 0.355, type: "tween" }}
        viewport={{ once: true }}
        className="text-center mt-10"
      >
        <h2 className="text-[#002082] text-xl md:text-3xl font-semibold">
          Our Core Pillars
        </h2>
        <div className="md:flex max-lg:flex-wrap justify-center gap-4 mt-3 md:mt-6">
          {corePillars.map((pillar) => (
            <div
              key={pillar.title}
              className="p-[22px] rounded-[13px] flex flex-col gap-2 items-center max-md:my-4 w-full md:w-[48%]"
              style={{ backgroundColor: pillar.hex }}
            >
              <Image
                src={pillar.image}
                alt={pillar.title}
                width={90}
                height={90}
                className="mix-blend-multiply mt-1"
              />
              <h3 className="max-md:text-2xl text-xl font-bold text-[#486284] max-md:leading-10">
                {pillar.title}
              </h3>
              <p className="text-[#8CA2C0] leading-tight">
                {pillar.description}
              </p>
            </div>
          ))}
        </div>

        <Button
          className="max-md:w-full mt-4 rounded-lg font-bold text-base md:px-10 md:py-6 max-lg:w-[70%] md:mt-6"
          onClick={() => {
            router.push("/bootcamps");
            mixpanel.track("explore_bootcamps", {
              location: "about",
            });
          }}
        >
          Explore Courses
        </Button>
      </motion.section>

      {/* ----------- FAQ Component ----------- */}
      <FAQ isAccHidden={true} />
    </Layout>
  );
};

export default About;
